import PropTypes from 'prop-types';
import { template } from 'lodash'
import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
// @mui
import { alpha, Box, Paper, Stack, Button,  Portal, Backdrop , InputBase, IconButton, Typography, Autocomplete, TextField, Avatar, Chip, Tooltip, FormControl, InputLabel, Select, MenuItem, ListItemText} from '@mui/material';
import {LoadingButton} from '@mui/lab';
// hooks
import { useBoolean } from 'src/hooks/use-boolean.js';
import { useResponsive } from 'src/hooks/useResponsive.js';
import { useSnackbar } from 'src/components/snackbar/index.js';
// components
import Iconify from "src/components/iconify/iconify.js";
import Editor from 'src/components/editor';
import { fDate } from 'src/util/formatTime.js';
import {Template1} from 'src/util/emailTemplate';
import {SEND_EMAIL} from 'src/graphql/modules/_client/sendemail.js';
import {GET_EMAIL_TEMPLATE} from 'src/graphql/modules/_client/emailTemplate.js';
import {DELETE_EMAIL_TEMPLATE} from 'src/graphql/modules/_client/emailTemplate.js';
import { useMutation, useQuery } from "@apollo/react-hooks";
import Scrollbar from 'src/components/scrollbar/scrollbar.js';
import MailTemplateDialog from './mail-template.js';
import { ConfirmDialog } from 'src/components/custom-dialog/index.js';


// ----------------------------------------------------------------------

const ZINDEX = 1998;

const POSITION = 24;

export default function MailCompose({ onCloseCompose, classdetails, credentials }) {
  const course = classdetails?.class;
  const smUp = useResponsive('up', 'sm');
  const trainerAss = course?.assessors?.map((res) => res)
  const trainee = classdetails?.class?.enrolment?.map((res) => res?.traineeInfo)
  const company = useSelector((state) => state?.auth?.company);
  const { databaseName}  = useSelector((state) => state?.auth?.companyProfile?.companyProfile);
  const [SendEmailMutation] =  useMutation(SEND_EMAIL);
  const [DeleteEmailTemplateMutation] =  useMutation(DELETE_EMAIL_TEMPLATE);
  const { enqueueSnackbar } = useSnackbar();
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false)
  const [_idToUpdate, setIdToUpdate] = useState(null);
  const [tempName, setTempName] = useState(null);
  const confirm = useBoolean();
  const onDelete = useBoolean();

  const [values, setValues] = useState({
    to: trainee,
    cc: trainerAss,
    subject: `Confirmed Class - ${course?.course?.courseTitle} on ${fDate(course?.courseDates.start)}` || '',
    emailMessage: Template1("iatos", classdetails) || '',
    credentials: credentials
  })

    const { data, loading, error, refetch } = useQuery(GET_EMAIL_TEMPLATE, {
      variables: {
        companyName: databaseName,
        },
        fetchPolicy: "cache-and-network",
      });
    
  
    useEffect(() => {
        if(data){
          setResults(data?.getEmailTemplate?.results)
        }
    }, [data])

  const [message, setMessage] = useState('');

  const fullScreen = useBoolean();

  const handleChangeMessage = useCallback((value) => {
    // setMessage(value);
    setValues({ ...values, emailMessage: value });
  }, []);

  const handleChangeTo = (event, newValue) => {
    setValues({ ...values, to: newValue });
  };
  const handleChangeCC = (event, newValue) => {
    setValues({ ...values, cc: newValue });
  };

  const handleSendEmail = (async() =>{
    try {
      setIsLoading(true)
      const {data: {sendEmail}} = await SendEmailMutation({
        variables: {
          companyName: company,
            data: {
              service: credentials?.Service,
              CLIENT_ID: credentials?.CLIENT_ID,
              CLIENT_SECRET: credentials?.CLIENT_SECRET,
              REFRESH_TOKEN: credentials?.REFRESH_TOKEN,
              REDIRECT_URI: credentials.REDIRECT_URI,
              TENANT_ID: credentials?.TENANT_ID,
              Email: credentials?.Email,
              Password: credentials?.Password,
              Authentication: credentials?.Authentication,
              Host: credentials?.Host,
              Port: credentials?.Port
            },
            mail: {
              from: credentials?.Email,
              to: values?.to?.map((res) => res?.email),
              cc: values?.cc?.map((res) => res?.email),
              subject: values?.subject,
              text: values?.emailMessage
            }
          }
      })
      if(sendEmail?.success){
        enqueueSnackbar("Email sent!", {variant: "success"});
        setIsLoading(false)
      }else{
        enqueueSnackbar("Server Error", {variant: "error"});
        setIsLoading(false)
      }
    } catch (error){
      enqueueSnackbar("Server Error", {variant: "error"});
      setIsLoading(false)
    }
  })

 const handleChangeTemplate = (event) => {
    const templ = event.target.value;

    const compiledSubject = template(templ?.subject || '');
    const compiledMessage = template(templ?.message || '');
    const subject = compiledSubject({ course, fDate });
    const emailMessage = compiledMessage({ course, fDate }); // or other variable(s)

     setValues({ ...values, 
      'subject': subject,
      'emailMessage': emailMessage
    });
    setIdToUpdate(templ?._id)
    setTempName(templ?.templateName)
 }

   const handleDelete = async() => {
     try {
       const {data: { deleteEmailTemplate }} = await DeleteEmailTemplateMutation({
         variables: {
           _id: _idToUpdate,
           companyName: databaseName
           }
       })
       if(deleteEmailTemplate?.success){
         enqueueSnackbar('Deleted successfully.', {variant: 'success'});
         refetch();
       }else {
         enqueueSnackbar('Failed to delete', {variant: 'error'});
       }
     } catch(err){
       console.log(err)
     }
   }


  return (
    <Portal>
      {(fullScreen.value || !smUp) && <Backdrop open />}

      <Paper
        sx={{
          right: 0,
          bottom: 0,
          borderRadius: 2,
          display: 'flex',
          position: 'fixed',
          zIndex: 1200,
          m: `${POSITION}px`,
          height: 'auto',
          flexDirection: 'column',
          boxShadow: (theme) => theme.customShadows.dropdown,
          ...(fullScreen.value
              ? {
                  m: 0,
                  right: POSITION / 2,
                  bottom: POSITION / 2,
                  width: `calc(100% - ${POSITION}px)`,
                  height: `calc(100% - ${POSITION}px)`,
                }
              : {
                  width: '620px',
                  height: '650px',
                }),

        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            bgcolor: 'background.neutral',
            p: (theme) => theme.spacing(1.5, 1, 1.5, 2)
          }}
        >
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            New Message
          </Typography>

          <IconButton onClick={fullScreen.onToggle}>
            <Iconify icon={fullScreen ? 'eva:collapse-fill' : 'eva:expand-fill'} />
          </IconButton>

          <IconButton onClick={onCloseCompose}>
            <Iconify icon="mingcute:close-line" />
          </IconButton>
        </Stack>

      <Box
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  px: 2,
                  pb: 2,
                  // Optional: custom scrollbar
                  '&::-webkit-scrollbar': {
                    width: 8,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    borderRadius: 4,
                  },
                }}
              >
              
                  <Autocomplete
                    multiple
                    id="trainee"
                    value={values?.to}
                    onChange={handleChangeTo}
                    options={trainee}
                    getOptionLabel={(option) => option?.fullName || "No Name"}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderOption={(props, _trainee) => (
                      <li {...props} key={_trainee._id}>
                        <Avatar
                          key={_trainee._id}
                          alt={_trainee.fullName}
                          src={_trainee.fullName}
                          sx={{ width: 24, height: 24, flexShrink: 0, mr: 1 }}
                        />
    
                        {_trainee.fullName}
                      </li>
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key} // Unique key for each chip
                            label={option.fullName}
                            {...tagProps}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="To" placeholder="Add trainee"/>
                    )}
                    sx={{
                      px: 2,
                      mb: 3,
                      mt: 3
                    }}                    
                  />
             

                <Autocomplete
                    multiple
                    id="trainerAssessor"
                    value={values?.cc}
                    onChange={handleChangeCC}
                    options={trainerAss}
                    getOptionLabel={(option) => option.fullName}
                    renderTags={(tagValue, getTagProps) =>
                      tagValue.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            label={option.fullName}
                            {...tagProps}
                          />
                        );
                      })
                    }
                    renderOption={(props, _trainerass) => (
                      <li {...props} key={_trainerass._id}>
                        <Avatar
                          key={_trainerass.id}
                          alt={_trainerass.fullName}
                          src={_trainerass.fullName}
                          sx={{ width: 24, height: 24, flexShrink: 0, mr: 1 }}
                        />
    
                        {_trainerass.fullName}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label="CC"/>
                    )}
                    sx={{
                      px: 2,
                      height: 48,
                      mb: 3
                    }}
                  />

                  <FormControl fullWidth sx={{ px: 2}}>
                    <InputLabel id="demo-simple-select-label" sx={{ml: 2}}>Select Template</InputLabel>
                    <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label="Select Template"
                        name="template"
                        renderValue={(selected) =>
                          selected?.templateName || ""
                         }
                        onChange={(e) => { handleChangeTemplate(e)}}
                    >  
                        {results?.map((temp) => {
                            return (
                                <MenuItem key={temp._id} value={temp}> 
                                 <ListItemText primary={temp.templateName} />
                                                <IconButton onClick={(e) => {
                                                  e.stopPropagation();
                                                  setIdToUpdate(temp._id) 
                                                  onDelete.onTrue()}} color='error'>
                            <Iconify icon="solar:trash-bin-trash-bold-duotone" />
                          </IconButton>
                                </MenuItem>
                                
                            )
                            })}

                    </Select>

        
                    </FormControl>


        <InputBase
          placeholder="Subject"
          sx={{
            px: 2,
            height: 48,
            borderBottom: (theme) => `solid 1px ${alpha(theme.palette.grey[500], 0.08)}`,
          }}
          value={values?.subject}
          fullWidth
        />

        <Stack spacing={2} flexGrow={1} sx={{ p: 2 }}>



          <Editor
            simple
            id="compose-mail"
            value={values?.emailMessage}
            onChange={handleChangeMessage}
            placeholder="Type a message"
            sx={{
              '& .ql-editor': {},
              ...(fullScreen.value && {
                height: 1,
                '& .quill': {
                  height: 1,
                },
                '& .ql-editor': {
                  maxHeight: 'unset',
                },
              }),
            }}
          />



          <Stack direction="row" alignItems="center">
            <Stack direction="row" alignItems="center" flexGrow={1}>
              <Tooltip title={_idToUpdate ? "Update Email Template" : "Create Email Template"} >
              <IconButton onClick={() => {
                confirm.onTrue()
              }}>
                <Iconify icon="solar:document-add-bold-duotone" />
              </IconButton>
              </Tooltip>
            </Stack>

            <LoadingButton
              variant="contained"
              color="primary"
              endIcon={<Iconify icon="iconamoon:send-fill" />}
              onClick={handleSendEmail}
              loading={isLoading}
            >
              Send
            </LoadingButton>
          </Stack>
        </Stack>
      </Box>

  
      </Paper>

         <MailTemplateDialog
              open={confirm.value}
              onClose={confirm.onFalse}
              title="Create Email Template"
              content={`Are you sure want to delete?`}
              classdetails={course}
              refreshQuery={refetch}
              _idToUpdate={_idToUpdate}
              emailDetails={values}
              tempName={tempName}
              action={
                <Button variant="contained" color="error" onClick={() => {
                  confirm.onFalse();
                }}>
                  Delete
                </Button>
              }
            />

                <ConfirmDialog
                    open={onDelete.value}
                    onClose={onDelete.onFalse}
                    title="Delete"
                    content={`Are you sure want to delete template?`}
                    action={
                      <Button variant="contained" color="error" onClick={() => {
                        handleDelete();
                        onDelete.onFalse();
                      }}>
                        Delete
                      </Button>
                    }
                  />

    </Portal>
  );
}

MailCompose.propTypes = {
  onCloseCompose: PropTypes.string,
  classdetails: PropTypes.any
};
